import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useOnboarding } from '@/hooks/useOnboarding'
import AppShell from '@/components/layout/AppShell'
import PulseView from '@/components/pulse/PulseView'
import OnboardingWizard from '@/components/onboarding/OnboardingWizard'
import ErrorBoundary from '@/components/shared/ErrorBoundary'
import { PulseProvider } from '@/contexts/PulseContext'

// Lazy-load heavy routes for code-splitting
const ConstellationView = lazy(() => import('@/components/constellation/ConstellationView'))
const DeepDiveView = lazy(() => import('@/components/deepdive/DeepDiveView'))
const EditorView = lazy(() => import('@/components/editor/EditorView'))
const ConflictsView = lazy(() => import('@/components/conflicts/ConflictsView'))
const EvolutionView = lazy(() => import('@/components/evolution/EvolutionView'))
const CanvasView = lazy(() => import('@/components/canvas/CanvasView'))
const LibraryView = lazy(() => import('@/components/library/LibraryView'))
const ResponseCanvasView = lazy(() => import('@/components/views/ResponseCanvasView'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

function LoadingFallback() {
  return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 loading-spinner border-3 mx-auto mb-3" />
        <p className="text-rsb-text-secondary text-sm">Carregando...</p>
      </div>
    </div>
  )
}

function DeepDiveRoute() {
  const { id } = useParams<{ id: string }>()
  if (!id) return <Navigate to="/" replace />
  return <DeepDiveView itemId={id} itemType="auto" />
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { needsOnboarding, needsSupabaseSetup, checking, completeOnboarding, recheckAfterSetup } = useOnboarding()

  if (checking) {
    return <LoadingFallback />
  }

  // Show onboarding wizard if Supabase is not configured or lenses need setup
  if (needsOnboarding) {
    return (
      <OnboardingWizard 
        onComplete={completeOnboarding} 
        needsSupabaseSetup={needsSupabaseSetup}
        onSupabaseConfigured={recheckAfterSetup}
      />
    )
  }

  return <>{children}</>
}

/**
 * Wrapper for all protected routes that need PulseProvider and AppShell.
 * This avoids duplicating the provider hierarchy for each route.
 */
function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <PulseProvider>
        <AppShell>
          <ErrorBoundary>{children}</ErrorBoundary>
        </AppShell>
      </PulseProvider>
    </ProtectedRoute>
  )
}

function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/settings" element={
            <ProtectedLayout><SettingsPage /></ProtectedLayout>
          } />
          <Route path="/" element={
            <ProtectedLayout><PulseView /></ProtectedLayout>
          } />
          <Route path="/constellation" element={
            <ProtectedLayout><ConstellationView /></ProtectedLayout>
          } />
          <Route path="/dive/:id" element={
            <ProtectedLayout><DeepDiveRoute /></ProtectedLayout>
          } />
          <Route path="/editor" element={
            <ProtectedLayout><EditorView /></ProtectedLayout>
          } />
          <Route path="/conflicts" element={
            <ProtectedLayout><ConflictsView /></ProtectedLayout>
          } />
          <Route path="/evolution" element={
            <ProtectedLayout><EvolutionView /></ProtectedLayout>
          } />
          <Route path="/canvas" element={
            <ProtectedLayout><CanvasView /></ProtectedLayout>
          } />
          <Route path="/library" element={
            <ProtectedLayout><LibraryView /></ProtectedLayout>
          } />
          <Route path="/visual-canvas" element={
            <ProtectedLayout><ResponseCanvasView /></ProtectedLayout>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
